/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * GridWidget
 */
define([
	'jquery',
	'underscore',
	'./GridEventTarget',
	'../VisView',
	'../VisEventHandler',
	'../../../widgets/livewidget/nls/StringResources'
], function($, _, GridEventTarget, VisView, VisEventHandler, StringResources) {
	'use strict';

	var GridView = VisView.extend({
		templateString: '<div class="dataview grid-view"  data-attach-point="contentNode"></div>',

		className: 'dataview grid-view',

		alwaysRenderOnResize: true,

		// Maps related to changing text properties
		// Map properties to css style strings
		PROPERTY_TO_STYLE: {
			'font-weight': 'bold',
			'font-style': 'italic',
			'text-decoration': 'underline'
		},
		CELL_VALUE_PROPERTY_MAP: {
			cellValueColor: 'color',
			cellValueFontSize: 'font-size',
			cellValueFontFace: 'font-family',
			cellValueFontBold: 'font-weight',
			cellValueFontItalic: 'font-style',
			cellValueFontUnderline: 'text-decoration',
			cellValueFontAlign: 'text-align'
		},
		COL_HEADING_PROPERTY_MAP: {
			columnHeadingColor: 'color',
			columnHeadingFontSize: 'font-size',
			columnHeadingFontFace: 'font-family',
			columnHeadingFontBold: 'font-weight',
			columnHeadingFontItalic: 'font-style',
			columnHeadingFontUnderline: 'text-decoration',
			columnHeadingFontAlign: 'text-align'
		},

		/**
		 * Cache focused group cell IDs
		 */
		_focusedCellIDs: {
			'key': null,
			'cellIDs': []
		},

		init: function() {
			GridView.inherited('init', this, arguments);
			this.isMobilePannable = true;

			if(this._isNewConditionalFormatFeature()){
				this._conditionalFormatting = this.content.getFeature('ConditionalFormatting');
			} else {
				// deregister here since the new component do not have access to the dashboardAPI to check whether the feature flag is turned on or off
				// Instead when the new feature gets loaded it always register itself with the 'Properties' and 'CustomColor' feature
				// Remove this code when the new feature get turn on by default
				const newCondFeature = this.content.getFeature('ConditionalFormatting');
				if (newCondFeature) {
					const properties = this.content.getFeature('Properties');
					const customColor = this.dashboardApi.getFeature('CustomColor');
					properties.deregisterProvider(newCondFeature);
					customColor.deregisterProvider(newCondFeature);
				}
			}
		},

		_isNewConditionalFormatFeature () {
			return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'xtabcondFormat', 'disabled');
		},

		_initializeConditionalFormatting: function() { // This is temporary until features have a proper way of initializing themselves
			return this._conditionalFormatting ? this._conditionalFormatting.initializeFeature() : Promise.resolve();
		},

		/**
		 * Loads and creates the control. Returns a promise which is resolved when the control
		 * is created and ready to render
		 */
		whenVisControlReady: function() {
			let result;
			if (this.visControl) {
				result = Promise.resolve(this.visControl);
			} else {
				//Load the control class defined in the item definition (eg JQGrid).
				result = new Promise((resolve, reject) => {
					try {
						require([this.visModel.getDefinition().control], (VisControl) => {
							this.visControl = new VisControl({
								domNode: this.$el.find('.grid-view')[0],
								gridView: this,
								tabNavigation: this.ownerWidget.interactivitySettings.tabNavigation,
								dashboardApi: this.dashboardApi,
								summaryFeature: this.content.getFeature('SummaryFeature'),
								conditionalFormatting: this._conditionalFormatting,
								cellRenderer : this.content.getFeature('CellRenderer.react')
							});
							this.visControl.reset(this.visModel);
							resolve(this.visControl);
						}, reject);
					} catch(error) {
						reject(error);
					}
				}).then(() => {
					this.eventHandler = new VisEventHandler({
						target: new GridEventTarget({
							$el: this.$el,
							visControl: this.visControl,
							visAPI: this.visModel
						}),
						dashboardSettings: this.dashboardSettings,
						transaction: this.transactionApi,
						logger: this.logger,
						ownerWidget: this.ownerWidget,
						visAPI: this.visModel,
						edgeSelection: true
					});
					return this.visControl;
				});
			}

			return result;
		},

		getCurrentViewSelector: function() {
			return this.eventHandler;
		},

		animate: function() {
			//Override the animate to avoid the fade in/out when we resize or apply filters
			if (this.resizing) {
				this.visModel.renderCompleteBeforeAnimation(); // Rendering is complete, no animation
			} else {
				GridView.inherited('animate', this, arguments);
			}
		},

		whenDataReady: function() {
			return GridView.inherited('whenDataReady', this, arguments).then(result => {
				if (result.data && result.data.getResult) {
					if(!result.sameQueryData) {
						this.visControl.reset(this.visModel);
						this.visControl.setData(result.data.getResult());
					}
				}
				return result;
			});
		},

		/**
		 * @param {Object} renderInfo - renderInfo passed to all render methods from the render sequence.
		 * @returns a promise which is resolved when render is complete
		 */
		render: function(renderInfo) {
			return this._initializeConditionalFormatting().then(() => {
				if (!this.isMappingComplete() || this.hasMissingFilters() || this.hasUnavailableMetadataColumns()) {
					this.removeGridView();
					this.resizeToWidget(renderInfo);
					this.renderIconView();
					return Promise.resolve(this);
				}

				if (!this.visControl) {
				//This should never happen because the render sequence ensures the control is loaded prior to calling render.
					return Promise.reject(new Error());
				}

				if (!this.visControl.hasData()) {
					return Promise.resolve(this);
				}

				this.removeIconView();

				this.resizeToWidget(renderInfo);

				var bResize = this.resizing;
				var renderingNewData = this.getRenderingNewData();

				var styleObj = {};
				styleObj.headerStyles = this._constructTextStyles(this.COL_HEADING_PROPERTY_MAP, this.visModel);
				styleObj.cellStyles = this._constructTextStyles(this.CELL_VALUE_PROPERTY_MAP, this.visModel);
				this.visControl.setStyles(styleObj);
				this.setSelections();
				return this.visControl.render(bResize, renderingNewData).then(
					(isRenderValid) => {
						if (!isRenderValid) {
							return { reRenderNeeded: true };
						} else {
						//Note: renderComplete is called in the base render method.
							return GridView.inherited('render', this, arguments);
						}
					}
				);
			});
		},

		_constructTextStyles: function (properties, visModel) {
			var stylesMap = {};
			var propertyToStyle = this.PROPERTY_TO_STYLE;
			_.each(properties, function (styleName, propertyName) {
				var propertyValue = visModel.getPropertyValue(propertyName);
				if(propertyValue){
					if(propertyToStyle[styleName]){
						propertyValue = propertyToStyle[styleName];
					}
					stylesMap[styleName] = propertyValue;
				}else{
					stylesMap[styleName] = '';
				}
			});
			return stylesMap;
		},

		getDescription: function() {
			// Append the F12 key instruction to the description
			var description = GridView.inherited('getDescription', this, arguments);
			return StringResources.get('WidgetLabelWithDescripion', {
				label: description,
				description: StringResources.get('f12KeyDescription')
			});
		},

		removeGridView: function() {
			this.visControl && this.visControl.removeGridView();
		},

		// @override
		remove: function() {
			if (this.eventHandler) {
				this.eventHandler.remove();
				this.eventHandler = null;
			}
			if (this.visControl) {
				this.visControl.remove();
				this.visControl = null;
			}
			GridView.inherited('remove', this, arguments);
		},

		_extendTuples: function(aSourceArray, aTargetArray, name, value) {
			_.each(aSourceArray, function(prevTuple) {
				var obj = {};
				obj[name] = value;
				aTargetArray.push(_.extend(obj, prevTuple));
			});
		},

		/**
		 * @returns an array that represents current filter as tuple
		 */
		_getSelectionValues: function() {
			let aTuple = [];

			const selections = this.getController();
			let colIndex = this.visControl.getRankDataItemCount();

			_.each(this.visualization.getSlots().getMappedSlotList(), function(slot) {
				if (slot.getId() !== 'heat') {
					_.each(slot.getDataItemList(), function (dataItem) {
						const values = selections.getSelectedTuples([dataItem.getColumnId()]);
						if (values && values.length) {
							let aTemp = [];
							_.each(values, function (value) {
								if (aTuple.length === 0) {
									let tuple = {};
									tuple[colIndex] = value;
									aTemp.push(tuple);
								} else {
									this._extendTuples(aTuple, aTemp, colIndex, value);
								}
							}.bind(this));
							aTuple = aTemp.slice();
						}
						colIndex++;
					}.bind(this));
				}
			}.bind(this));

			return aTuple;
		},

		/**
		 * Update the selection tuple of the grid
		 */
		setSelections(){
			this.visControl.setSelections(this._getSelectionValues());
		},

		/**
		 * Update the selection tuple of the grid and update the view
		 * this will be called from visView once grid view is ready
		 * should never be called before view is ready
		 */
		renderSelected() {
			this.setSelections();
			if(this.visControl){
				// force a re-render so selections are handled properly
				this.visControl.onResize();
			}
		},

		getFocusedCellIDs: function() {
			return this._focusedCellIDs;
		},

		setFocusedCellIDs: function(cellID, cells) {
			this._focusedCellIDs.key = cellID;
			this._focusedCellIDs.cellIDs = cells;
		},

		/**
		 * Handle the on container entered event
		 * Sets up keyboard navigation inside the list
		 */
		onEnterContainer: function() {
			this.visControl.onEnterContainer();
		},

		filterNodeKeydownHandler: function(event) {
			event.stopPropagation();
		}

	});

	return GridView;
});
